import React, { useContext } from "react";
import { createPortal } from "react-dom";

import styled from "styled-components";
import { TbCloudUpload, TbInfoCircle, TbVideo } from "react-icons/tb";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useState, useRef, useEffect, useCallback } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { AiFillCloseCircle } from "react-icons/ai";
import axios from "axios";
import { useLazyQuery } from "@apollo/client";

import theme from "theme/themeThree";
import { BaseSection } from "../styledComponents";
import { Toggle } from "MVVM/Components/Toggle";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import {
  QuickEditPreferredFinalTouches,
  QuickEditPreferredThumbnail,
} from "MVVM/State/quickEdit";
import { usePortal } from "MVVM/Hooks/usePortal";
import { SignatureSelectorModal } from "MVVM/Components/Signatures";
import { analytics } from "MVVM/Analytics";
import { IQuickEditMoment } from "../@types";
import { useUser } from "MVVM/Hooks/useUser";
import { Button } from "../../Button";
import { Tooltip } from "MVVM/Components/Tooltip";
import { GET_MY_SIGNATURE_QUICK_EDIT } from "MVVM/GraphQL/quickEdit";
import { TClipCard } from "MVVM/State/loadoutSelector";
import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { EPaywallType } from "MVVM/Components/Modals/Paywall";
import { TooltipPortalContext } from "../index";

export const FinalTouches = ({
  selectedFinalTouches,
  setSelectedFinalTouches,
  setThumbnailKey,
  mediaUploadUrl,
  isOwnMoment,
  moment,
  hideSignature,
  disablePreferredTouches,
  closeModal,
}: {
  selectedFinalTouches: string[];
  setSelectedFinalTouches: (finalTouches: string[]) => void;
  setThumbnailKey: (thumbnailKey: string) => void;
  mediaUploadUrl?: {
    fields: {
      name: string;
      value: string;
    }[];
    key: string;
    url: string;
  };
  isOwnMoment: boolean;
  moment: IQuickEditMoment;
  hideSignature?: boolean;
  disablePreferredTouches?: boolean;
  closeModal?: () => void;
}) => {
  const { allstarUser } = useUser();
  const setAllstarModalState = useSetRecoilState(AllstarModalState);
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [preferredThumbnail, setPreferredThumbnail] = useRecoilState(
    QuickEditPreferredThumbnail,
  );
  const [preferredFinalTouches, setPreferredFinalTouches] = useRecoilState(
    QuickEditPreferredFinalTouches,
  );
  const [userHasSignature, setUserHasSignature] = useState<boolean | null>(
    null,
  );
  const [userHasSignatureMusic, setUserHasSignatureMusic] = useState<
    boolean | null
  >(null);
  const [getMySignature] = useLazyQuery(GET_MY_SIGNATURE_QUICK_EDIT, {
    fetchPolicy: "network-only",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const isSecondary = ["secondary", "secondary_fail"].includes(moment.type);
  const signatureDisabled = !isOwnMoment;
  const rapidFireDisabled = isSecondary;
  const contextCamsDisabled = isSecondary;

  const isPremiumDisabled =
    allstarUser?.user?.userGroup?.type === "FREE" ||
    allstarUser?.user?.userGroup?.type === "ESSENTIALS";
  const hideWatermarkDisabled = isPremiumDisabled;
  const hideKillFeedDisabled = isPremiumDisabled;
  const mountedCamerasDisabled = isPremiumDisabled;
  const finalKillCameraDisabled = isPremiumDisabled;
  const disableJumpCutsDisabled = isPremiumDisabled;

  const isCounterStrike = moment.game === "CS2";

  const fetchUserSignature = useCallback(() => {
    if (allstarUser?.user?._id) {
      getMySignature({
        variables: {
          user: allstarUser.user._id,
          signature: true,
        },
      }).then(({ data }) => {
        const hasSignature =
          (data?.getLoadouts?.metadataCustomStyleSelection?.length ?? 0) > 0;

        const hasMusicSelected =
          hasSignature &&
          data.getLoadouts?.clipCards?.some(
            (clipCard: TClipCard) => clipCard.card.effect === "FACET_LOOKUP",
          );

        setUserHasSignature(hasSignature);
        setUserHasSignatureMusic(hasMusicSelected);
      });
    }
  }, [allstarUser?.user?._id, getMySignature]);

  useEffect(() => {
    if (allstarUser?.loggedIn && userHasSignature === null) {
      fetchUserSignature();
    }
  }, [allstarUser?.loggedIn, userHasSignature, fetchUserSignature]);

  const {
    portalOpen: signatureSelectorOpen,
    setPortalOpen: setSignatureSelectorOpen,
    Portal: SignatureSelectorPortal,
  } = usePortal(
    <SignatureSelectorModal
      onSave={() => {
        setSignatureSelectorOpen(false);
        fetchUserSignature();
      }}
      onCloseClick={() => {
        setSignatureSelectorOpen(false);
      }}
    />,
  );

  useEffect(() => {
    if (preferredThumbnail && !disablePreferredTouches) {
      setSelectedFileUrl(preferredThumbnail);
      setThumbnailKey(preferredThumbnail);
    }
  }, [preferredThumbnail, setThumbnailKey, disablePreferredTouches]);

  useEffect(() => {
    if (disablePreferredTouches) return;
    let preloadedTouches = preferredFinalTouches;
    if (signatureDisabled)
      preloadedTouches = preloadedTouches.filter(
        (t) => t !== "signature" && t !== "music",
      );
    if (rapidFireDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "rapidFire");
    if (contextCamsDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "contextCams");
    if (hideWatermarkDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "hideWatermark");
    if (hideKillFeedDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "hideKillFeed");
    if (mountedCamerasDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "mountedCameras");
    if (finalKillCameraDisabled)
      preloadedTouches = preloadedTouches.filter(
        (t) => t !== "finalKillCamera",
      );
    if (disableJumpCutsDisabled)
      preloadedTouches = preloadedTouches.filter(
        (t) => t !== "disableJumpCuts",
      );
    if (userHasSignature === false)
      preloadedTouches = preloadedTouches.filter((t) => t !== "signature");
    setSelectedFinalTouches(preloadedTouches);
  }, [
    preferredFinalTouches,
    signatureDisabled,
    rapidFireDisabled,
    contextCamsDisabled,
    hideWatermarkDisabled,
    hideKillFeedDisabled,
    mountedCamerasDisabled,
    finalKillCameraDisabled,
    disableJumpCutsDisabled,
    setSelectedFinalTouches,
    disablePreferredTouches,
    userHasSignature,
  ]);

  const { REACT_APP_S3_CLIP_BUCKET } = process.env;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploading(true);
      const fileUrl = URL.createObjectURL(file);
      const formData = new FormData();

      for (const field of mediaUploadUrl?.fields ?? []) {
        formData.append(field.name, field.value ?? "");
      }
      formData.append("file", file);

      try {
        await axios.post(mediaUploadUrl?.url ?? "", formData);

        setSelectedFileUrl(fileUrl);
        if (mediaUploadUrl?.key) {
          setThumbnailKey(`${mediaUploadUrl.url}/${mediaUploadUrl.key}`);
        }
        setUploading(false);
      } catch (error) {
        setUploading(false);
      }
    }
  };

  const handleThumbnailClick = () => {
    fileInputRef.current?.click();
  };

  const handlePremiumFeatureClick = (featureName: string) => {
    analytics.track("Upgrade CTA Clicked", {
      location: `Quick Edit - ${featureName}`,
    });
    if (closeModal) {
      closeModal();
    }
    setAllstarModalState({
      data: { type: EPaywallType.signature as EPaywallType },
      isOpen: ModalType.Paywall,
      functions: {},
    });
  };

  const handleToggle = (touch: string) => {
    if (selectedFinalTouches.includes(touch)) {
      setPreferredFinalTouches(
        preferredFinalTouches.filter((t) => t !== touch),
      );
      setSelectedFinalTouches(selectedFinalTouches.filter((t) => t !== touch));
    } else {
      if (touch === "signature" && userHasSignature === false) {
        analytics.track("Signature - Open Modal", {
          where: "Quick Edit",
          type: "First Time Setup",
        });
        setSignatureSelectorOpen(true);
      }
      setPreferredFinalTouches([...preferredFinalTouches, touch]);
      setSelectedFinalTouches([...selectedFinalTouches, touch]);
    }
  };

  const {
    portalOpen: thumbnailSelectorOpen,
    setPortalOpen: setThumbnailSelectorOpen,
    Portal: ThumbnailSelectorPortal,
  } = usePortal(
    <ThumbnailSection>
      <Text
        variant="text.labelSmall"
        style={{
          width: "100%",
          justifyContent: "start",
          color: theme.colors.chalk,
        }}
      >
        Clip Thumbnail:
      </Text>

      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: "none" }}
      />

      <Flex style={{ position: "relative", width: "100%" }}>
        <ThumbnailArea
          onClick={handleThumbnailClick}
          showBorder={!selectedFileUrl}
        >
          {selectedFileUrl ? (
            <PreviewThumbnail src={selectedFileUrl} />
          ) : (
            <>
              {uploading ? (
                <LoadingSpinner height={30} width={30} />
              ) : (
                <Flex
                  flexDirection={"column"}
                  alignItems={"center"}
                  style={{
                    gap: 8,
                  }}
                >
                  <UploadIcon size={30} />
                  <Text variant="text.labelMedium" color={theme.colors.chalk}>
                    Customize Your Thumbnail
                  </Text>
                  <Text
                    variant="text.labelXXSmall"
                    color={theme.colors.chalk}
                    textAlign={"center"}
                    maxWidth={300}
                  >
                    Upload an optional PNG, JPEG or GIF as a custom thumbnail,
                    or we'll use your Signature or current loadout.
                  </Text>
                </Flex>
              )}
            </>
          )}
        </ThumbnailArea>
      </Flex>
      <Flex
        style={{
          position: "relative",
          width: "100%",
          gap: 12,
          justifyContent: "center",
          alignItems: "center",
          paddingTop: 12,
        }}
      >
        <Button
          classification="roundedFilledAlt"
          size="small"
          onClick={() => {
            setPreferredThumbnail("");
            setSelectedFileUrl(null);
            setThumbnailKey("");
            setThumbnailSelectorOpen(false);
          }}
          flex={true}
        >
          Cancel
        </Button>
        <Button
          classification="roundedFilled"
          size="small"
          flex={true}
          disabled={!selectedFileUrl}
          onClick={() => setThumbnailSelectorOpen(false)}
        >
          Save
        </Button>
      </Flex>
    </ThumbnailSection>,
  );

  return (
    <>
      {signatureSelectorOpen && SignatureSelectorPortal}
      {thumbnailSelectorOpen && ThumbnailSelectorPortal}
      <Section>
        <Text
          variant="text.labelSmall"
          style={{
            width: "100%",
            justifyContent: "start",
            color: theme.colors.chalk,
          }}
        >
          Choose your finishing touches:
        </Text>
        <ToggleContainer>
          <ToggleColumn>
            <ToggleRow>
              <FeatureTooltip
                title="Set Custom Thumbnail"
                description="By default, your clips will have a thumbnail selected in your Signature or loadout. Upload your own to add some extra personalization."
              >
                <Flex alignItems="center" style={{ gap: 4 }}>
                  <Text variant="text.labelSmall">Set Custom Thumbnail</Text>
                  <InfoTooltipIcon />
                </Flex>
              </FeatureTooltip>
              <Flex
                style={{
                  justifyContent: "start",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {selectedFileUrl ? (
                  <RemoveText
                    variant="text.labelSmall"
                    color="alert"
                    onClick={() => {
                      setPreferredThumbnail("");
                      setSelectedFileUrl(null);
                      setThumbnailKey("");
                    }}
                  >
                    Remove
                    <AiFillCloseCircle
                      style={{
                        width: 18,
                        height: 18,
                        marginLeft: 8,
                        verticalAlign: "middle",
                        color: theme.colors.alert,
                      }}
                    />
                  </RemoveText>
                ) : (
                  <SignatureText
                    color="chalk"
                    variant="text.labelSmall"
                    onClick={() => {
                      analytics.track("Thumbnail - Open Modal", {
                        where: "Quick Edit",
                        type: "Customize",
                      });
                      setThumbnailSelectorOpen(true);
                    }}
                  >
                    Upload
                  </SignatureText>
                )}
              </Flex>
            </ToggleRow>
          </ToggleColumn>

          {!hideSignature && (
            <>
              <ToggleColumn>
                <ToggleRow>
                  <FeatureTooltip
                    title="Add My Signature"
                    description="Signatures allow you to add a personalized intro/nameplate/outro to your clips, to show off your stats to viewers."
                  >
                    <Flex alignItems="center" style={{ gap: 4 }}>
                      <Text variant="text.labelSmall">Add My Signature</Text>
                      <InfoTooltipIcon />
                    </Flex>
                  </FeatureTooltip>
                  <Toggle
                    width={48}
                    checked={selectedFinalTouches.includes("signature")}
                    disabled={signatureDisabled}
                    onChange={() => {
                      handleToggle("signature");
                    }}
                  />
                </ToggleRow>
                {signatureDisabled && (
                  <Text
                    variant="text.labelXXSmall"
                    color={theme.colors.error}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <InfoIcon style={{ marginTop: 2 }} />
                    Signatures are not yet supported for Moments that are not
                    your own.
                  </Text>
                )}
                <ManageSignatureLink
                  variant="text.labelSmall"
                  color="chalkAlpha60"
                  onClick={() => {
                    analytics.track("Signature - Open Modal", {
                      where: "Quick Edit",
                      type: "Manage",
                    });
                    setSignatureSelectorOpen(true);
                  }}
                >
                  <Image
                    src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/loadouts/sig.svg`}
                    height={12}
                    width={12}
                    mr="6px"
                  />
                  Manage My Signature
                </ManageSignatureLink>
              </ToggleColumn>
              <ToggleColumn>
                <ToggleRow>
                  <FeatureTooltip
                    title="Add Signature Music"
                    description="If you have music genres selected in your Signature, we'll choose a matching song to add to your clips."
                  >
                    <Flex alignItems="center" style={{ gap: 4 }}>
                      <Text variant="text.labelSmall">Add Signature Music</Text>
                      <InfoTooltipIcon />
                    </Flex>
                  </FeatureTooltip>

                  <Toggle
                    width={48}
                    checked={selectedFinalTouches.includes("music")}
                    disabled={!selectedFinalTouches.includes("signature")}
                    onChange={() => {
                      handleToggle("music");
                    }}
                  />
                </ToggleRow>

                {signatureDisabled && (
                  <Text
                    variant="text.labelXXSmall"
                    color={theme.colors.error}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <InfoIcon style={{ marginTop: 2 }} />
                    Signatures are not yet supported for Moments that are not
                    your own.
                  </Text>
                )}

                {!signatureDisabled &&
                  !selectedFinalTouches.includes("signature") &&
                  selectedFinalTouches.includes("music") && (
                    <WarningText
                      variant="text.labelXXSmall"
                      color={theme.colors.alert}
                    >
                      Signature must be enabled to use music. Enable "Add My
                      Signature" above.
                    </WarningText>
                  )}

                {!signatureDisabled &&
                  selectedFinalTouches.includes("signature") &&
                  userHasSignatureMusic === false &&
                  selectedFinalTouches.includes("music") && (
                    <WarningText
                      variant="text.labelXXSmall"
                      color={theme.colors.alert}
                    >
                      You do not have music genres selected in your Signature.
                      Click "Manage My Signature" above to add genres.
                    </WarningText>
                  )}
              </ToggleColumn>
            </>
          )}
          {isCounterStrike && (
            <ToggleColumn>
              <ToggleRow>
                <VideoFeatureTooltip
                  title="Add Context Cams"
                  description="Before a gunfight that ends with 1 or more kills, we'll show a raised camera view of the area, giving your viewers extra context about what happened."
                  videoSrc="cards/thumbnails/specialfx/mp4/envcams_thumbnail.mp4"
                >
                  <Flex alignItems="center" style={{ gap: 6 }}>
                    <Text variant="text.labelSmall">Add Context Cams</Text>
                    <VideoPreviewIcon />
                  </Flex>
                </VideoFeatureTooltip>
                <Toggle
                  width={48}
                  checked={selectedFinalTouches.includes("contextCams")}
                  disabled={contextCamsDisabled}
                  onChange={() => {
                    handleToggle("contextCams");
                  }}
                />
              </ToggleRow>
              {contextCamsDisabled && (
                <Text
                  variant="text.labelXXSmall"
                  color={theme.colors.error}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <InfoIcon style={{ marginTop: 2 }} />
                  Context Cams are not yet supported for Fail Moments.
                </Text>
              )}
            </ToggleColumn>
          )}

          {isCounterStrike && (
            <ToggleColumn>
              <ToggleRow>
                <FeatureTooltip
                  title="Rapid Fire"
                  description="Love Jump Cuts, but want something even faster? Enable Rapid Fire to see just the kills. Obviously, this does not work with Disable Jump Cuts enabled."
                >
                  <Flex alignItems="center" style={{ gap: 4 }}>
                    <Text variant="text.labelSmall">Rapid Fire</Text>
                    <InfoTooltipIcon />
                  </Flex>
                </FeatureTooltip>
                <Toggle
                  width={48}
                  checked={selectedFinalTouches.includes("rapidFire")}
                  disabled={
                    rapidFireDisabled ||
                    selectedFinalTouches.includes("disableJumpCuts")
                  }
                  onChange={() => {
                    handleToggle("rapidFire");
                  }}
                />
              </ToggleRow>
              {(rapidFireDisabled ||
                selectedFinalTouches.includes("disableJumpCuts")) && (
                <Text
                  variant="text.labelXXSmall"
                  color={theme.colors.error}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 4,
                    whiteSpace: "normal",
                    flexWrap: "wrap",
                  }}
                >
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <InfoIcon style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box style={{ marginLeft: 6 }}>
                      {rapidFireDisabled
                        ? "Rapid Fire is not yet supported for Fail Moments."
                        : "Rapid Fire is not supported when Disabled Jump Cuts is enabled."}
                    </Box>
                  </Box>
                </Text>
              )}
            </ToggleColumn>
          )}

          {/* Pro Only Section */}
          <ProDivider>
            <ProGradientOverlay />
            <Flex
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              style={{ position: "relative", zIndex: 1 }}
            >
              <DividerLine />
              <Flex
                alignItems="center"
                flexDirection="column"
                style={{ gap: 4 }}
              >
                <DividerText variant="text.labelXSmall">
                  Pro Features
                </DividerText>
                {isPremiumDisabled && (
                  <UpgradeText
                    variant="text.labelXXSmall"
                    color="chalkAlpha80"
                    onClick={() => handlePremiumFeatureClick("Pro Features")}
                  >
                    Upgrade to unlock these features
                  </UpgradeText>
                )}
              </Flex>
              <DividerLine />
            </Flex>
          </ProDivider>

          {/* Hide Watermark - Available for all games */}
          <PremiumFeatureWrapper
            isDisabled={hideWatermarkDisabled}
            featureName="Hide Watermark"
            onPremiumClick={handlePremiumFeatureClick}
          >
            <ToggleRow>
              <VideoFeatureTooltip
                title="Hide Watermark"
                description="Remove the Allstar watermark from your clips for a cleaner look. This feature is only available for Pro users."
                videoSrc="cards/watermarks/no_watermark_mp4/v1/ui/no_watermark_mp4.mp4"
              >
                <Flex alignItems="center" style={{ gap: 6 }}>
                  <Text variant="text.labelSmall">Hide Watermark</Text>
                  <VideoPreviewIcon />
                </Flex>
              </VideoFeatureTooltip>
              <Toggle
                width={48}
                checked={selectedFinalTouches.includes("hideWatermark")}
                disabled={hideWatermarkDisabled}
                onChange={() => {
                  handleToggle("hideWatermark");
                }}
              />
            </ToggleRow>
          </PremiumFeatureWrapper>

          {/* CS2 Pro Features */}
          {isCounterStrike && (
            <>
              <PremiumFeatureWrapper
                isDisabled={mountedCamerasDisabled}
                featureName="Camera Effects"
                onPremiumClick={handlePremiumFeatureClick}
              >
                <ToggleRow>
                  <VideoFeatureTooltip
                    title="Add Camera Effects"
                    description="Enable player follow cams for more cinematic shots."
                    videoSrc="cards/thumbnails/specialfx/mp4/guncam_thumbnail.mp4"
                  >
                    <Flex alignItems="center" style={{ gap: 6 }}>
                      <Text variant="text.labelSmall">Add Camera Effects</Text>
                      <VideoPreviewIcon />
                    </Flex>
                  </VideoFeatureTooltip>
                  <Toggle
                    width={48}
                    checked={selectedFinalTouches.includes("mountedCameras")}
                    disabled={mountedCamerasDisabled}
                    onChange={() => {
                      handleToggle("mountedCameras");
                    }}
                  />
                </ToggleRow>
              </PremiumFeatureWrapper>

              <PremiumFeatureWrapper
                isDisabled={finalKillCameraDisabled}
                featureName="Final Kill Cam"
                onPremiumClick={handlePremiumFeatureClick}
              >
                <ToggleRow>
                  <FeatureTooltip
                    title="Final Kill Cam"
                    description="On the last kill, we'll switch views to the enemy player. BONUS: When enabled, this will hide your deaths at the end of the clip!"
                  >
                    <Flex alignItems="center" style={{ gap: 4 }}>
                      <Text variant="text.labelSmall">Final Kill Cam</Text>
                      <InfoTooltipIcon />
                    </Flex>
                  </FeatureTooltip>
                  <Toggle
                    width={48}
                    checked={selectedFinalTouches.includes("finalKillCamera")}
                    disabled={finalKillCameraDisabled}
                    onChange={() => {
                      handleToggle("finalKillCamera");
                    }}
                  />
                </ToggleRow>
              </PremiumFeatureWrapper>

              <PremiumFeatureWrapper
                isDisabled={
                  disableJumpCutsDisabled &&
                  !selectedFinalTouches.includes("rapidFire")
                }
                featureName="Disable Jump Cuts"
                onPremiumClick={handlePremiumFeatureClick}
              >
                <ToggleRow>
                  <FeatureTooltip
                    title="Disable Jump Cuts"
                    description="By default, Allstar cuts your clips to the best moments, with optional transitions between them. Use this to show the full sequence from first-to-last kill. Does not work with Rapid Fire enabled."
                  >
                    <Flex alignItems="center" style={{ gap: 4 }}>
                      <Text variant="text.labelSmall">Disable Jump Cuts</Text>
                      <InfoTooltipIcon />
                    </Flex>
                  </FeatureTooltip>
                  <Toggle
                    width={48}
                    checked={selectedFinalTouches.includes("disableJumpCuts")}
                    disabled={
                      disableJumpCutsDisabled ||
                      selectedFinalTouches.includes("rapidFire")
                    }
                    onChange={() => {
                      handleToggle("disableJumpCuts");
                    }}
                  />
                </ToggleRow>
                {selectedFinalTouches.includes("rapidFire") && (
                  <Text
                    variant="text.labelXXSmall"
                    color={theme.colors.error}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 4,
                      whiteSpace: "normal",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box style={{ display: "flex", alignItems: "center" }}>
                      <InfoIcon style={{ marginTop: 2, flexShrink: 0 }} />
                      <Box style={{ marginLeft: 6 }}>
                        Disabling Jump Cuts is not supported when Rapid Fire is
                        enabled.
                      </Box>
                    </Box>
                  </Text>
                )}
              </PremiumFeatureWrapper>

              <PremiumFeatureWrapper
                isDisabled={hideKillFeedDisabled}
                featureName="Hide Kill Feed"
                onPremiumClick={handlePremiumFeatureClick}
              >
                <ToggleRow>
                  <VideoFeatureTooltip
                    title="Hide Kill Feed"
                    description="Remove the kill feed from your clips for a cleaner viewing experience."
                    videoSrc="cards/overlay/hidekillfeed_mp4/v1/ui/hidekillfeed_mp4.mp4"
                  >
                    <Flex alignItems="center" style={{ gap: 6 }}>
                      <Text variant="text.labelSmall">Hide Kill Feed</Text>
                      <VideoPreviewIcon />
                    </Flex>
                  </VideoFeatureTooltip>
                  <Toggle
                    width={48}
                    checked={selectedFinalTouches.includes("hideKillFeed")}
                    disabled={hideKillFeedDisabled}
                    onChange={() => {
                      handleToggle("hideKillFeed");
                    }}
                  />
                </ToggleRow>
              </PremiumFeatureWrapper>
            </>
          )}
        </ToggleContainer>
      </Section>
    </>
  );
};

const FeatureTooltip = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => {
  const portalTarget = useContext(TooltipPortalContext);
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isHovered && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const containerRect = portalTarget?.getBoundingClientRect();
      if (containerRect) {
        setPosition({
          top: rect.bottom - containerRect.top + 8,
          left: rect.left - containerRect.left + rect.width / 2,
        });
      }
    }
  }, [isHovered, portalTarget]);

  const tooltipContent = (
    <PortalTooltipContent
      style={{ top: position.top, left: position.left }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TooltipContent>
        <TooltipTitle variant="text.labelSmall" color="ice" fontWeight={600}>
          {title}
        </TooltipTitle>
        <TooltipDescription variant="text.labelXSmall" color="chalkAlpha80">
          {description}
        </TooltipDescription>
      </TooltipContent>
    </PortalTooltipContent>
  );

  return (
    <TooltipTrigger
      ref={triggerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && portalTarget && createPortal(tooltipContent, portalTarget)}
    </TooltipTrigger>
  );
};

const VideoFeatureTooltip = ({
  title,
  description,
  videoSrc,
  children,
}: {
  title: string;
  description: string;
  videoSrc: string;
  children: React.ReactNode;
}) => {
  const portalTarget = useContext(TooltipPortalContext);
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const videoUrl = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/${videoSrc}`;

  useEffect(() => {
    if (isHovered && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const containerRect = portalTarget?.getBoundingClientRect();
      if (containerRect) {
        setPosition({
          top: rect.bottom - containerRect.top + 8,
          left: rect.left - containerRect.left + rect.width / 2,
        });
      }
    }
  }, [isHovered, portalTarget]);

  const tooltipContent = (
    <PortalTooltipContent
      style={{ top: position.top, left: position.left }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <VideoTooltipContent>
        <TooltipTitle variant="text.labelSmall" color="ice" fontWeight={600}>
          {title}
        </TooltipTitle>
        <TooltipDescription variant="text.labelXSmall" color="chalkAlpha80">
          {description}
        </TooltipDescription>
        <VideoPreviewContainer>
          <VideoPreview src={videoUrl} autoPlay loop muted playsInline />
        </VideoPreviewContainer>
      </VideoTooltipContent>
    </PortalTooltipContent>
  );

  return (
    <TooltipTrigger
      ref={triggerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && portalTarget && createPortal(tooltipContent, portalTarget)}
    </TooltipTrigger>
  );
};

const TooltipTrigger = styled.div`
  display: inline-block;
  cursor: pointer;
`;

const PortalTooltipContent = styled.div`
  position: absolute;
  transform: translateX(-50%);
  z-index: 1000;
  pointer-events: auto;
  animation: tooltipFadeIn 200ms ease-out;

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const TooltipContent = styled(Flex)`
  align-items: start;
  background-color: ${theme.colors.grey850};
  border-radius: 8px;
  border: 1px solid ${theme.colors.chalkAlpha20};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  width: max-content;
`;

const VideoTooltipContent = styled(Flex)`
  align-items: start;
  background-color: ${theme.colors.grey850};
  border-radius: 8px;
  border: 1px solid ${theme.colors.chalkAlpha20};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  width: 280px;
`;

const VideoPreviewContainer = styled(Box)`
  aspect-ratio: 16 / 9;
  background-color: ${theme.colors.navy};
  border-radius: 6px;
  overflow: hidden;
  width: 100%;
`;

const VideoPreview = styled.video`
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const TooltipTitle = styled(Text)`
  line-height: 1.2;
  text-align: left;
`;

const TooltipDescription = styled(Text)`
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  max-width: 200px;
`;

const ManageSignatureLink = styled(Text)`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  line-height: 1.4;
  padding-top: 4px;
  padding-bottom: 4px;
  text-align: center;
  transition: color 100ms ease;

  &:hover {
    color: ${theme.colors.envy};
  }
`;

const InfoIcon = styled(TbInfoCircle)`
  color: ${theme.colors.error};
`;

const InfoTooltipIcon = styled(TbInfoCircle)`
  color: ${theme.colors.chalkAlpha60};
  cursor: pointer;
  height: 18px;
  width: 18px;
  transition:
    color 120ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${theme.colors.envy};
    transform: scale(1.1);
  }
`;

const VideoPreviewIcon = styled(TbVideo)`
  color: ${theme.colors.chalkAlpha60};
  cursor: pointer;
  height: 18px;
  width: 18px;
  transition:
    color 120ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${theme.colors.envy};
    transform: scale(1.1);
  }
`;

const ToggleContainer = styled(Flex)`
  background-color: ${(props) => props.theme.colors.grey825};
  border-radius: 8px;
  flex-direction: column;
  width: 100%;
`;

const ToggleRow = styled(Flex)<{ clickable?: boolean }>`
  align-items: center;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  justify-content: space-between;
  width: 100%;
`;

const ToggleColumn = styled(Flex)`
  align-items: flex-start;
  border-bottom: 1px solid ${(props) => props.theme.colors.ned};
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 24px;
  width: 100%;
  &:last-child {
    border-bottom: none;
  }
`;

const ProToggleColumn = styled(ToggleColumn)<{
  clickable?: boolean;
  onClick?: () => void;
}>`
  background: linear-gradient(
    90deg,
    ${theme.colors.premiumPurpleAlpha05} 0%,
    ${theme.colors.premiumPurpleAlpha03} 50%,
    transparent 100%
  );
  border-left: 2px solid ${theme.colors.premiumPurpleAlpha40};
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  position: relative;
`;

const Section = styled(BaseSection)`
  align-items: center;
  border-bottom: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 100%;
`;

const ThumbnailSection = styled(BaseSection)`
  align-items: center;
  background-color: ${theme.colors.grey850};
  border-bottom: none;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  padding: 24px;
  width: 25vw;
  @media (max-width: ${(props) => props.theme.breaks.small}) {
    width: 80vw;
  }
`;

const PreviewThumbnail = styled(Image)`
  border-radius: 12px;
  height: 100%;
  width: auto;
`;

const UploadIcon = styled(TbCloudUpload)`
  color: ${theme.colors.chalkAlpha80};
`;

const ThumbnailArea = styled(Flex)<{ showBorder: boolean }>`
  align-items: center;
  aspect-ratio: 16 / 9;
  background-color: ${theme.colors.grey850};
  border-radius: 12px;
  border: 1px dashed ${theme.colors.chalkAlpha40};
  cursor: pointer;
  height: 100%;
  justify-content: center;
  width: 100%;
  &:hover {
    border: 1px dashed ${theme.colors.envy};
    & > svg {
      color: ${theme.colors.envy};
    }
  }
`;

export const SignatureText = styled(Text)`
  transition: color 100ms ease;
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.envy};
  }
`;

export const RemoveText = styled(Text)`
  transition: color 100ms ease;
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.alert};
  }
`;

const WarningText = styled(Text)`
  align-items: center;
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
  margin-top: 4px;
`;

const ProDivider = styled(Flex)`
  align-items: center;
  background: linear-gradient(
    180deg,
    ${theme.colors.premiumPurpleAlpha10} 0%,
    ${theme.colors.premiumPurpleAlpha05} 50%,
    ${theme.colors.premiumPurpleAlpha03} 100%
  );
  gap: 12px;
  justify-content: center;
  overflow: hidden;
  padding: 20px 24px;
  position: relative;
  width: 100%;
`;

const ProGradientOverlay = styled(Box)`
  background: radial-gradient(
    ellipse at center,
    ${theme.colors.premiumPurpleAlpha20} 0%,
    ${theme.colors.premiumPurpleAlpha10} 40%,
    ${theme.colors.premiumPurpleAlpha05} 70%,
    transparent 100%
  );
  filter: blur(15px);
  height: 60px;
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 100%;
`;

const DividerLine = styled(Box)`
  background: linear-gradient(
    90deg,
    transparent 0%,
    ${theme.colors.chalkAlpha20} 50%,
    transparent 100%
  );
  flex: 1;
  height: 1px;
`;

const DividerText = styled(Text)`
  color: ${theme.colors.premiumPurple};
  font-weight: 700;
  letter-spacing: 1px;
  text-shadow: 0 0 12px ${theme.colors.premiumPurpleAlpha40};
  text-transform: uppercase;
  white-space: nowrap;
`;

const UpgradeText = styled(Text)`
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: ${theme.colors.premiumPurpleAlpha30};
  transition: all 120ms ease;

  &:hover {
    color: ${theme.colors.ice};
    text-decoration-color: ${theme.colors.premiumPurpleAlpha80};
  }
`;

interface IPremiumFeatureWrapperProps {
  isDisabled: boolean;
  featureName: string;
  onPremiumClick: (name: string) => void;
  children: React.ReactNode;
}

export const PremiumFeatureWrapper: React.FC<IPremiumFeatureWrapperProps> = ({
  isDisabled,
  featureName,
  onPremiumClick,
  children,
}) => {
  return (
    <ProToggleColumn
      clickable={isDisabled}
      onClick={() => isDisabled && onPremiumClick(featureName)}
    >
      {children}
    </ProToggleColumn>
  );
};
